import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { config, PaymentResource, OrderRequest, BasePaymentComponent } from '@fgb/core';
import { DateTime } from 'luxon';

declare var createOrder: any;

/* ================================
 * Types
 * ================================ */

class GoAheadItem {
  productName: string;
  productPrice: number;
  productId: string;
  quantity: number;

  constructor(productName: string, productPrice: number, productId: string, quantity: number) {
    this.productName = productName;
    this.productPrice = productPrice;
    this.productId = productId;
    this.quantity = quantity;
  }
}

interface GoAheadTaxFee {
  type: number;
  title: string;
  amount: number;
}

@Component({
  selector: 'fgbcl-payment-go-ahead-pay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payment-go-ahead-pay.component.html',
  styleUrl: './payment-go-ahead-pay.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PaymentGoAheadPayComponent extends BasePaymentComponent {
  config: any = config;

  /* ================================
   * Public API
   * ================================ */

  init() {
    const cartItems =
      this.paymentRequest?.items.map((item: any) => new GoAheadItem(item.name, item.unitPrice / 100, item.id, item.qty)) || [];
    const requestBody = this.buildRequestBody(this.context, cartItems, 'en-US', 'USD');
    createOrder(requestBody);
  }

  submit(): void {}

  /* ================================
   * Private Helpers
   * ================================ */

  private buildRequestBody(
    ctx: ReturnType<typeof this.buildContext>,
    cartItems: readonly GoAheadItem[],
    lang: string,
    currency: string
  ): Record<string, any> {
    return {
      clientKey: ctx.properties.clientKey,
      tenantId: ctx.properties.tenantId,
      locationId: ctx.properties.locationId,
      externalVendorId: ctx.properties.externalVendorId,
      'X-API-Key': '',
      lang,
      currency,
      'X-Auth-Provider': '',
      Authorization: '',
      urlRedirect: ctx.callbackUrl,
      transactionDetails: Object.assign(this.initializeTransaction(this.mapCartItems(cartItems)), {
        externalVendorId: ctx.properties.externalVendorId,
      }),
    };
  }

  private initializeTransaction(contents: GoAheadItem[]) {
    return {
      orderId: this.orderId,
      orderTime: DateTime.now().toUTC().toISO(),
      contents,
      otherTaxFees: [] as GoAheadTaxFee[],
      discounts: [],
      tip: 0,
      grandTotal: this.amount, // display amount from the api response
    };
  }

  private mapCartItems(items: readonly GoAheadItem[]) {
    return items.map((item: GoAheadItem) => ({ ...item, totalPrice: item.productPrice * item.quantity }));
  }

  protected override buildContext(paymentRequest: OrderRequest, resources: PaymentResource[]) {
    return {
      properties: this.getProperties(resources),
      scripts: ['https://venueframework.s3.us-west-2.amazonaws.com/vzpay/stage/v1.2/vzpaysdk.js'],
      allowedOrigins: ['https://pwa-uat.venuetize.com', 'https://pwa-stg.venuetize.com', window.location.origin],
      callbackUrl: this.config.portalUrl + paymentRequest?.callbackUrl + `?orderId=${this.orderId}&origin=external` || '',
      successUrl: paymentRequest?.successUrl || '',
      failureUrl: paymentRequest?.failureUrl || '',
    };
  }
}
